import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config";
import ImpactPanel from "../components/ImpactPanel";

const PLANTED_STATUSES = ["planted", "paid", "zelle_paid", "gpay_paid"];
const fmtDate = (iso) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";

// All Sri Taru farms we can see from space, plus the overall impact.
function FarmsFromSpace() {
  const [index, setIndex] = useState(null);
  const [treesByYear, setTreesByYear] = useState(null);

  useEffect(() => {
    fetch("/space/index.json").then((r) => (r.ok ? r.json() : null)).then(setIndex).catch(() => setIndex(null));
    supabase
      .from("sritaru_contributions")
      .select("num_trees, payment_status, year")
      .then(({ data }) => {
        const byYear = {};
        (data || []).filter((r) => PLANTED_STATUSES.includes(r.payment_status) && r.year).forEach((r) => {
          byYear[r.year] = (byYear[r.year] || 0) + (r.num_trees || 0);
        });
        setTreesByYear(byYear);
      });
  }, []);

  return (
    <div className="py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-sritaru-800">🛰️ Our farms from space</h1>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            SankalpTaru records where every tree is planted. We follow those farms with satellites: sharp photos
            every year or two, and a greenness reading every few days, so you can watch the trees take root.
          </p>
        </div>

        {treesByYear && <ImpactPanel treesByYear={treesByYear} />}

        <section>
          <h2 className="text-xl font-bold text-sritaru-700 mb-3">2024 planting</h2>
          {!index && <p className="text-gray-500 text-sm">Loading farms...</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(index?.farms || []).map((f) => (
              <Link key={f.id} to={`/farm/${f.id}`}
                    className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow border border-transparent hover:border-sritaru-300">
                <p className="font-bold text-sritaru-800">{f.farmer}</p>
                <p className="text-sm text-gray-600">{f.village}, {f.district} · {f.state}</p>
                <p className="text-sm text-gray-700 mt-2">{f.trees_on_farm.toLocaleString()} {f.species} trees · planted {fmtDate(f.planted)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {f.passes} satellite readings{f.latest_highres ? ` · sharp photo ${fmtDate(f.latest_highres)}` : ""}
                </p>
                <p className="text-sm text-sritaru-700 font-semibold mt-2">See it from space →</p>
              </Link>
            ))}
            {(index?.farms_without_location || []).map((f) => (
              <div key={f.farmer} className="bg-white/70 rounded-xl p-4 border border-dashed border-gray-300">
                <p className="font-bold text-gray-700">{f.farmer}</p>
                <p className="text-sm text-gray-500">{f.village}, {f.district} · {f.state}</p>
                <p className="text-sm text-gray-600 mt-2">{f.trees_on_farm.toLocaleString()} {f.species} trees</p>
                <p className="text-xs text-gray-400 mt-1">Satellite view coming once we have the planting location.</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-md p-5">
          <h2 className="text-xl font-bold text-sritaru-700 mb-1">2025 and 2026 plantings</h2>
          <p className="text-sm text-gray-600">
            We've shared the 2025 and 2026 names with SankalpTaru. As they register each tree with its location,
            those farms will appear here too.
          </p>
        </section>

        {index?.sources && (
          <p className="text-[10px] text-gray-400 text-center">
            {index.sources.sentinel}. {index.sources.wayback}. Updated {index.updated}.
          </p>
        )}
      </div>
    </div>
  );
}

export default FarmsFromSpace;
